import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { provideIcons } from "@ng-icons/core";
import { lucideLoaderCircle, lucideEye, lucideEyeOff } from "@ng-icons/lucide";
import { HlmButtonDirective } from "@spartan-ng/ui-button-helm";
import { HlmInputDirective } from "@spartan-ng/ui-input-helm";
import { HlmLabelDirective } from "@spartan-ng/ui-label-helm";
import { resetPasswordErrorMap } from "../../../constants/authenticate.constants";
import {
  AuthenticateStateEnum,
  ResetPasswordEnum,
} from "../../../enums/authenticate.enum";
import { CognitoResetPasswordResult } from "../../../interfaces/auth.interfaces";
import { AuthService } from "../../../services/auth.service";
import { HlmSpinnerComponent } from "@spartan-ng/ui-spinner-helm";
import { PasswordRequirementsComponent } from "@keus-template/ui";
import {
  HlmTabsComponent,
  HlmTabsContentDirective,
} from "@spartan-ng/ui-tabs-helm";
import {
  HlmCardContentDirective,
  HlmCardDirective,
  HlmCardFooterDirective,
} from "@spartan-ng/ui-card-helm";
import { HlmFormFieldModule } from "@spartan-ng/ui-formfield-helm";
import { passwordMatchValidator } from "@keus-template/validators";
import { BrnInputOtpComponent } from "@spartan-ng/brain/input-otp";
import {
  HlmInputOtpDirective,
  HlmInputOtpGroupDirective,
  HlmInputOtpSlotComponent,
} from "../../../../../ui/ui-inputotp-helm/src";
import { NgIcon } from "@ng-icons/core";
import { HlmIconDirective } from "@spartan-ng/ui-icon-helm";

@Component({
  selector: "keus-reset-password",
  imports: [
    CommonModule,
    HlmButtonDirective,
    HlmInputDirective,
    FormsModule,
    ReactiveFormsModule,
    HlmLabelDirective,
    HlmSpinnerComponent,
    HlmTabsComponent,
    HlmTabsContentDirective,
    HlmCardContentDirective,
    HlmCardDirective,
    HlmCardFooterDirective,
    HlmFormFieldModule,
    PasswordRequirementsComponent,
    BrnInputOtpComponent,
    HlmInputOtpDirective,
    HlmInputOtpGroupDirective,
    HlmInputOtpSlotComponent,
    NgIcon,
    HlmIconDirective,
  ],
  templateUrl: "./reset-password.component.html",
  providers: [provideIcons({ lucideLoaderCircle, lucideEye, lucideEyeOff })],
  host: {
    class: "block",
  },
  styleUrl: "./reset-password.component.css",
})
export class ResetPasswordComponent implements OnInit {
  /** Injected AuthService instance */
  private readonly _authService: AuthService = inject(AuthService);

  /** The reset password enum */
  public readonly resetPasswordEnum: typeof ResetPasswordEnum =
    ResetPasswordEnum;

  /** Signal indicating whether the confirm sign up form is loading */
  public readonly isLoading: WritableSignal<boolean> = signal(false);

  /** Signal indicating the state of the reset password process */
  public readonly resetPasswordState: WritableSignal<
    ResetPasswordEnum | undefined
  > = signal(undefined);

  /** The error message */
  public errorMessage: string | undefined;

  /** Show password signals for different fields */
  public readonly showPassword = signal<boolean>(false);
  public readonly showConfirmPassword = signal<boolean>(false);

  /** The reset password event */
  @Output()
  public readonly resetPasswordEvent: EventEmitter<AuthenticateStateEnum> =
    new EventEmitter<AuthenticateStateEnum>();

  /** The email form */
  public readonly emailForm = new FormGroup({
    email: new FormControl<string>("", {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
  });

  /** The reset password form */
  public readonly resetPasswordForm = new FormGroup({
    code: new FormControl<string>("", {
      nonNullable: true,
      validators: [Validators.required],
    }),
    password: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(
          /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/,
        ),
      ],
    }),
    confirmPassword: new FormControl<string>("", {
      nonNullable: true,
    }),
  });

  /** Overrides global formatDate (spartan)  */
  public readonly transformPaste = (pastedText: string) =>
    pastedText.replaceAll("-", "");

  /**
   * Initialize the component.
   */
  ngOnInit() {
    this.resetPasswordForm.controls.confirmPassword.setValidators([
      Validators.required,
      passwordMatchValidator(this.resetPasswordForm.controls.password),
    ]);
    this.resetPasswordForm.controls.confirmPassword.updateValueAndValidity();

    // Re-validate confirmPassword when password changes
    this.resetPasswordForm.controls.password.valueChanges.subscribe(() => {
      this.resetPasswordForm.controls.confirmPassword.updateValueAndValidity();
    });
  }

  /**
   * Sends the code to reset the password.
   */
  public sendCodeResetPassword() {
    if (!this.emailForm.valid) {
      this.emailForm.controls.email.setErrors({ invalid: true });
      return;
    }
    const { email } = this.emailForm.getRawValue();
    this.isLoading.set(true);

    this._authService.resetPassword$(email).subscribe({
      next: (result) => {
        this.resetPasswordState.set(result.type);
      },
      error: (error: CognitoResetPasswordResult) => {
        this.isLoading.set(false);
        this.resetPasswordState.set(ResetPasswordEnum.FAIL);
        this.errorMessage = resetPasswordErrorMap.get(error.result?.name);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Confirms new password.
   */
  public confirmNewPassword(): void {
    if (!this.resetPasswordForm.valid) {
      this.resetPasswordForm.controls.code.setErrors({ invalid: true });
      this.resetPasswordForm.controls.password.setErrors({ invalid: true });
      return;
    }
    const { email } = this.emailForm.getRawValue();
    const { code, password } = this.resetPasswordForm.getRawValue();
    this.isLoading.set(true);

    this._authService.confirmNewPassword$(email, code, password).subscribe({
      next: () => this.onSignInClick(),
      error: (error) => {
        console.error(error);
        this.isLoading.set(false);
      },
      complete: () => this.isLoading.set(false),
    });
  }

  /**
   * Handle the sign in click.
   */
  public onSignInClick(): void {
    this.resetPasswordEvent.emit(AuthenticateStateEnum.SIGN_IN_AND_SIGN_UP);
  }

  /**
   * Handle the back to email click.
   */
  public onBackToEmail(): void {
    this.resetPasswordState.set(undefined);
  }

  public checkPasswordStateToShowTab(): string {
    return this.resetPasswordState() === undefined
      ? "email"
      : this.resetPasswordState() ===
          this.resetPasswordEnum.INPUT_VERIFICATION_CODE
        ? "password"
        : "email";
  }

  /**
   * Toggle password visibility
   */
  public togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  /**
   * Toggle confirm password visibility
   */
  public toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }
}
